import { useQuery } from "@tanstack/react-query";
import { Flame, Globe2, ListChecks, Phone, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

import { listLeads } from "../api/leads.ts";
import { listSites } from "../api/sites.ts";
import { listCalls } from "../api/calls.ts";

export default function DashboardPage() {
  const { data: leads }          = useQuery({ queryKey: ["leads", "dashboard"],      queryFn: () => listLeads({ limit: 1 }) });
  const { data: newLeads }       = useQuery({ queryKey: ["leads", "new"],            queryFn: () => listLeads({ status: "new", limit: 1 }) });
  const { data: assignedLeads }  = useQuery({ queryKey: ["leads", "assigned"],       queryFn: () => listLeads({ status: "assigned_to_tech", limit: 1 }) });
  const { data: sites }          = useQuery({ queryKey: ["sites"],                   queryFn: listSites });
  const { data: calls }          = useQuery({ queryKey: ["calls", "dashboard"],      queryFn: () => listCalls({ limit: 1 }) });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">Welcome back — here's what's happening today.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={ListChecks} label="Total leads"        value={leads?.total ?? "—"}         color="brand"  cta={<Link to="/leads">View all →</Link>} />
        <StatCard icon={Flame}      label="New leads"          value={newLeads?.total ?? "—"}      color="amber"  cta={<Link to="/leads?status=new">Work queue →</Link>} />
        <StatCard icon={TrendingUp} label="Converted to Job"   value={assignedLeads?.total ?? "—"} color="red"    cta={<Link to="/leads?status=assigned_to_tech">View →</Link>} />
        <StatCard icon={Phone}      label="Total calls"        value={calls?.total ?? "—"}         color="violet" cta={<Link to="/calls">View →</Link>} />
      </div>

      {/* Sites overview */}
      {sites && sites.length > 0 && (
        <div className="card">
          <h2 className="section-title">Active sites</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sites.map(s => (
              <div key={s.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <Globe2 className="h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">{s.name}</p>
                  <p className="truncate text-xs text-slate-500">{s.city ?? s.domain ?? "—"}</p>
                </div>
                <span className={`ml-auto badge ${s.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                  {s.is_active ? "Live" : "Off"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Getting started */}
      <div className="card">
        <h2 className="section-title">Getting started</h2>
        <ol className="mt-1 list-inside list-decimal space-y-2.5 text-sm text-slate-600">
          <li>Add a site under <Link to="/sites" className="font-medium text-brand-600 hover:underline">Sites</Link> — copy the API key and webhook secret.</li>
          <li>Install the WebSquad WordPress plugin and paste the values in its settings page.</li>
          <li>Submit a Contact Form 7 test — it should appear in <Link to="/leads" className="font-medium text-brand-600 hover:underline">Leads</Link> within 3 seconds.</li>
          <li>Under <Link to="/settings" className="font-medium text-brand-600 hover:underline">Settings</Link>, enroll 2FA and add a passkey for your account.</li>
        </ol>
      </div>
    </div>
  );
}

const colorMap = {
  brand:  { bg: "bg-brand-50",  icon: "text-brand-600",  stat: "text-brand-700",  cta: "text-brand-600 hover:text-brand-700" },
  amber:  { bg: "bg-amber-50",  icon: "text-amber-600",  stat: "text-amber-700",  cta: "text-amber-600 hover:text-amber-700" },
  red:    { bg: "bg-red-50",    icon: "text-red-500",    stat: "text-red-700",    cta: "text-red-600 hover:text-red-700" },
  violet: { bg: "bg-violet-50", icon: "text-violet-600", stat: "text-violet-700", cta: "text-violet-600 hover:text-violet-700" },
};

function StatCard({ icon: Icon, label, value, color, cta }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string; value: number | string;
  color: keyof typeof colorMap;
  cta: React.ReactNode;
}) {
  const c = colorMap[color];
  return (
    <div className="card flex flex-col gap-3">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${c.bg}`}>
        <Icon className={`h-5 w-5 ${c.icon}`} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
        <p className={`mt-0.5 text-3xl font-bold ${c.stat}`}>{value}</p>
      </div>
      <div className={`text-xs font-semibold ${c.cta}`}>{cta}</div>
    </div>
  );
}
